import type { NotificationFeed } from '@levitas/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@levitas/ui';
import { Bell } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAgora } from '../../lib/agora';
import { api } from '../../lib/api';

/** "há 5 min", "ontem" — tempo relativo, que é como a pessoa pensa. */
function quando(iso: string, agora: number): string {
  const minutos = Math.floor((agora - new Date(iso).getTime()) / 60_000);

  if (minutos < 1) return 'agora';
  if (minutos < 60) return `há ${minutos} min`;

  const horas = Math.floor(minutos / 60);
  if (horas < 24) return `há ${horas}h`;

  const dias = Math.floor(horas / 24);
  return dias === 1 ? 'ontem' : `há ${dias} dias`;
}

/**
 * O sino: o registro do que a igreja comunicou (M12). Ele não se desliga, e é
 * onde a mensagem sempre está — mesmo para quem recusou push e e-mail.
 */
export function Sino() {
  const navegar = useNavigate();
  const clienteQuery = useQueryClient();
  const [aberto, setAberto] = useState(false);
  const caixa = useRef<HTMLDivElement>(null);
  // O relógio vem do hook: ler `Date.now()` na renderização torna o componente
  // impuro e o tempo relativo instável.
  const agora = useAgora();

  const { data } = useQuery({
    queryKey: ['sino'],
    queryFn: () => api<NotificationFeed>('/me/notifications'),
    // Sem canal em tempo real ainda: uma olhada por minuto basta para o sino.
    refetchInterval: 60_000,
    retry: false,
  });

  const marcar = useMutation({
    mutationFn: (id?: string) =>
      api<{ lidas: number }>(`/me/notifications/read${id ? `?id=${id}` : ''}`, { method: 'POST' }),
    onSuccess: async () => {
      await clienteQuery.invalidateQueries({ queryKey: ['sino'] });
    },
  });

  useEffect(() => {
    if (!aberto) return;

    function fechar(evento: MouseEvent) {
      if (!caixa.current?.contains(evento.target as Node)) setAberto(false);
    }

    document.addEventListener('mousedown', fechar);
    return () => document.removeEventListener('mousedown', fechar);
  }, [aberto]);

  const naoLidas = data?.naoLidas ?? 0;

  return (
    <div className="relative" ref={caixa}>
      <Button
        variante="fantasma"
        tamanho="icone"
        aria-label={naoLidas > 0 ? `Avisos (${naoLidas} não lidos)` : 'Avisos'}
        aria-expanded={aberto}
        data-testid="sino"
        onClick={() => setAberto((atual) => !atual)}
      >
        <span className="relative">
          <Bell aria-hidden className="size-4" />
          {naoLidas > 0 ? (
            <span
              className="absolute -right-1.5 -top-1.5 flex min-w-4 items-center justify-center rounded-full bg-[var(--status-recusado)] px-1 text-2xs font-semibold text-white"
              data-testid="sino-nao-lidas"
            >
              {naoLidas > 9 ? '9+' : naoLidas}
            </span>
          ) : null}
        </span>
      </Button>

      {aberto ? (
        <div
          className="absolute right-0 top-12 z-30 max-h-[70vh] w-[min(22rem,calc(100vw-2rem))] overflow-y-auto rounded-[var(--radius-lg)] border border-[var(--cor-borda)] bg-[var(--cor-superficie)] shadow-[var(--shadow-lg)]"
          data-testid="caixa-de-avisos"
        >
          <div className="flex items-center justify-between border-b border-[var(--cor-borda)] px-4 py-3">
            <p className="text-sm font-semibold">Avisos</p>
            {naoLidas > 0 ? (
              <button
                type="button"
                onClick={() => marcar.mutate(undefined)}
                className="min-h-11 text-sm text-[var(--brand)]"
              >
                Marcar todas como lidas
              </button>
            ) : null}
          </div>

          {(data?.items.length ?? 0) === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-[var(--cor-texto-suave)]">
              Nada por aqui ainda. Quando a igreja comunicar algo, aparece neste sino.
            </p>
          ) : (
            <ul className="divide-y divide-[var(--cor-borda)]">
              {data!.items.map((aviso) => (
                <li key={aviso.id}>
                  <button
                    type="button"
                    data-testid={`aviso-${aviso.id}`}
                    onClick={() => {
                      marcar.mutate(aviso.id);
                      setAberto(false);
                      if (aviso.link) navegar(aviso.link);
                    }}
                    className={`flex w-full flex-col gap-0.5 px-4 py-3 text-left transition hover:bg-[var(--cor-superficie-hover)] ${
                      aviso.readAt ? '' : 'bg-[var(--brand-suave)]/40'
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      {!aviso.readAt ? (
                        <span
                          aria-hidden
                          className="size-1.5 shrink-0 rounded-full bg-[var(--brand)]"
                        />
                      ) : null}
                      <span className="text-sm font-medium">{aviso.title}</span>
                    </span>
                    <span className="text-sm text-[var(--cor-texto-suave)]">{aviso.body}</span>
                    <span className="text-xs text-[var(--cor-texto-suave)]">
                      {quando(aviso.createdAt, agora)}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}
    </div>
  );
}
